/**
 * Phase 7c.4 — IPAM ↔ ZeroConf wizard glue.
 *
 * A tiny Zustand store that holds the "linked client" context for the wizard
 * (kept separate from `wizardStore.ts` to avoid touching the big state file
 * and to keep concerns isolated). Plus `applyIpamPrefillToStore`, which
 * translates an IPAM prefill payload (from
 * `GET /api/ipam/clients/{cid}/wizard-prefill`) into the wizard's own
 * vendor/model/VLAN/DNS shape via the existing public setters.
 *
 * The mapping is intentionally a soft "load + edit" — nothing is locked,
 * everything is still editable in the wizard.
 */
import { create } from "zustand";
import { useWizardStore } from "./wizardStore";

export type WizardSource = "blank" | "from_client";

interface WizardClientLink {
    client_id: string | null;
    client_name: string | null;
    source: WizardSource;
    setClientLink: (id: string, name: string) => void;
    clearClientLink: () => void;
}

export const useWizardClientLink = create<WizardClientLink>((set) => ({
    client_id: null,
    client_name: null,
    source: "blank",
    setClientLink: (id, name) => set({ client_id: id, client_name: name, source: "from_client" }),
    clearClientLink: () => set({ client_id: null, client_name: null, source: "blank" }),
}));


// ============================================================
// Prefill mapping
// ============================================================

interface FirewallHint {
    id: string;
    vendor: string;
    model: string;
    cpe?: string | null;
    hostname?: string | null;
    ip_address?: string | null;
    subnet_id?: string | null;
}

interface SubnetHint {
    id: string;
    cidr: string;
    vlan_id?: number | null;
    gateway: string;
    dns_servers: string[];
    site?: string | null;
    name?: string | null;
}

export interface WizardPrefillData {
    client: { id: string; name: string; slug?: string; industry?: string };
    firewalls: FirewallHint[];
    subnets_by_purpose: Record<string, SubnetHint[]>;
    dns_candidates: string[];
    gateway_candidates: { ip: string; subnet_cidr: string | null }[];
}

export interface PrefillReport {
    applied: string[]; // human-readable list, e.g. ["vendor=fortinet", "VLAN 10 LAN HQ Milano"]
    warnings: string[];
}

/** Map IPAM subnet.purpose → wizard VLAN type. We pick the closest semantic
 *  bucket; "voip" has no first-class wizard type so we fall back to "server"
 *  (a phone PBX is essentially a server). */
const PURPOSE_TO_VLAN_TYPE: Record<string, "corporate" | "guest" | "server" | "management" | "iot"> = {
    lan: "corporate",
    dmz: "server",
    guest: "guest",
    iot: "iot",
    management: "management",
    voip: "server",
};

/** Pick the "primary" subnet of a purpose when there are several. Heuristic:
 *  1. site/name contains "hq" or "main" wins
 *  2. otherwise the widest CIDR (smallest prefix length) wins (e.g. /24 over /28)
 *  3. fallback to first in list. */
function pickPrimary(list: SubnetHint[]): SubnetHint {
    if (list.length === 1) return list[0];
    const tagged = list.find((s) => {
        const blob = `${s.site || ""} ${s.name || ""}`.toLowerCase();
        return blob.includes("hq") || blob.includes("main") || blob.includes("primary");
    });
    if (tagged) return tagged;
    const sorted = [...list].sort((a, b) => {
        const pa = parseInt((a.cidr.split("/")[1] || "32"), 10);
        const pb = parseInt((b.cidr.split("/")[1] || "32"), 10);
        return pa - pb;
    });
    return sorted[0];
}

/** Apply an IPAM prefill payload into the wizard's Zustand state. Returns a
 *  small report so the UI can show toasts about what landed and what didn't. */
export function applyIpamPrefillToStore(data: WizardPrefillData): PrefillReport {
    const report: PrefillReport = { applied: [], warnings: [] };
    const store = useWizardStore.getState();

    // --- Vendor / Model / Hostname --------------------------------------
    if (data.firewalls && data.firewalls.length > 0) {
        // For now we pick the first; the UI surfaces a chooser when there's >1
        // and re-applies the prefill after the user picks one.
        const fw = data.firewalls[0];
        const v = (fw.vendor || "").toLowerCase();
        if (["fortinet", "watchguard", "zyxel", "pfsense"].includes(v)) {
            store.updateStep1({
                vendor: v as any,
                modello: fw.model || "",
                hostname: fw.hostname || "",
            });
            report.applied.push(`vendor ${fw.vendor} / model ${fw.model}`);
        } else if (fw.vendor) {
            report.warnings.push(`Vendor "${fw.vendor}" isn't supported by the wizard — pick one manually`);
        }
        if (data.firewalls.length > 1) {
            report.warnings.push(
                `Client has ${data.firewalls.length} firewalls — loaded the most recent. Use the picker to switch.`,
            );
        }
    }

    // --- DNS -----------------------------------------------------------
    // Priority: dns_candidates (Important IPs role=dns/dc) → first subnet's
    // dns_servers → leave whatever default the wizard already had.
    let primaryDns = "";
    let secondaryDns = "";
    if (data.dns_candidates && data.dns_candidates.length > 0) {
        primaryDns = data.dns_candidates[0];
        secondaryDns = data.dns_candidates[1] || "";
    }
    if (!primaryDns) {
        const firstBucket = Object.values(data.subnets_by_purpose || {})[0];
        const firstSubnet = firstBucket && firstBucket[0];
        if (firstSubnet && firstSubnet.dns_servers && firstSubnet.dns_servers.length > 0) {
            primaryDns = firstSubnet.dns_servers[0];
            secondaryDns = firstSubnet.dns_servers[1] || "";
        }
    }
    if (primaryDns) {
        store.updateStep1({ dns_primary: primaryDns, dns_secondary: secondaryDns || "8.8.8.8" });
        report.applied.push(`DNS ${primaryDns}${secondaryDns ? " + " + secondaryDns : ""}`);
    }

    // --- VLANs (from subnets_by_purpose) -------------------------------
    const vlans: any[] = [];
    const seenVlanIds = new Set<string>();
    let mgmtSubnet: SubnetHint | null = null;
    for (const [purpose, list] of Object.entries(data.subnets_by_purpose || {})) {
        if (!list || list.length === 0) continue;
        const primary = pickPrimary(list);

        // Capture management for the wizard's dedicated managementNetwork field.
        if (purpose === "management" && !mgmtSubnet) {
            mgmtSubnet = primary;
        }

        const vlanType = PURPOSE_TO_VLAN_TYPE[purpose] || "corporate";
        const vlanIdStr = primary.vlan_id != null ? String(primary.vlan_id) : "";
        // If two subnets ended up wanting the same VLAN ID, warn — the wizard
        // doesn't allow duplicates downstream.
        if (vlanIdStr) {
            if (seenVlanIds.has(vlanIdStr)) {
                report.warnings.push(
                    `VLAN ${vlanIdStr} conflict between purposes — kept the first, drop or renumber the duplicate`,
                );
                continue;
            }
            seenVlanIds.add(vlanIdStr);
        }
        // Convert CIDR -> network_ip + subnet_mask the wizard expects.
        const [network_ip, prefixStr] = primary.cidr.split("/");
        const prefix = parseInt(prefixStr || "24", 10);
        const subnet_mask = prefixToMask(prefix);

        vlans.push({
            nome_vlan: primary.name || `${purpose.toUpperCase()}`,
            vlan_id: vlanIdStr || (vlans.length ? String(10 + vlans.length * 10) : "10"),
            network_ip,
            gateway_ip: primary.gateway || firstHostOf(primary.cidr),
            subnet_mask,
            enable_dhcp: true,
            dns_primary: primaryDns || (primary.dns_servers[0] || ""),
            dns_secondary: secondaryDns || (primary.dns_servers[1] || ""),
            porte_assegnate: [],
            vlan_type: vlanType,
        });
        report.applied.push(
            `VLAN ${vlanIdStr || "(auto)"} · ${primary.name || primary.cidr} (${purpose})`,
        );
        if (list.length > 1) {
            const extras = list.length - 1;
            report.warnings.push(
                `${extras} extra ${purpose.toUpperCase()} subnet(s) skipped — the wizard accepts one per purpose`,
            );
        }
    }
    if (vlans.length > 0) {
        store.updateStep4(vlans);
    } else {
        report.warnings.push(
            "No subnets matched a known purpose (lan/dmz/guest/iot/management/voip) — VLAN section left blank",
        );
    }

    // --- Management network -------------------------------------------
    if (mgmtSubnet) {
        const [mgmtIp] = mgmtSubnet.cidr.split("/");
        store.setManagementNetwork({
            enabled: true,
            ipAddress: mgmtSubnet.gateway || firstHostOf(mgmtSubnet.cidr),
            subnetMask: prefixToMask(parseInt(mgmtSubnet.cidr.split("/")[1] || "24", 10)),
            dhcpEnabled: false,
        });
        // mgmtIp is intentionally unused here — the wizard uses gateway + mask;
        // we keep the destructure to make the intent obvious for future edits.
        void mgmtIp;
        report.applied.push(`Management network ${mgmtSubnet.cidr}`);
    }

    return report;
}


// --- helpers ----------------------------------------------------------

function prefixToMask(prefix: number): string {
    if (prefix < 0 || prefix > 32) return "255.255.255.0";
    const mask = prefix === 0 ? 0 : ((~0 << (32 - prefix)) >>> 0);
    return [(mask >>> 24) & 0xff, (mask >>> 16) & 0xff, (mask >>> 8) & 0xff, mask & 0xff].join(".");
}

function firstHostOf(cidr: string): string {
    const [ip, prefStr] = cidr.split("/");
    const parts = ip.split(".").map((p) => parseInt(p, 10));
    if (parts.length !== 4 || parts.some(isNaN)) return ip;
    const prefix = parseInt(prefStr || "32", 10);
    const ipInt = ((parts[0] << 24) | (parts[1] << 16) | (parts[2] << 8) | parts[3]) >>> 0;
    const mask = prefix === 0 ? 0 : ((~0 << (32 - prefix)) >>> 0);
    const net = ipInt & mask;
    const firstHost = (net + 1) >>> 0;
    return [
        (firstHost >>> 24) & 0xff,
        (firstHost >>> 16) & 0xff,
        (firstHost >>> 8) & 0xff,
        firstHost & 0xff,
    ].join(".");
}
